/**
 * @file SupplierEditDialog.tsx
 * @description Create / edit поставщика (phase08.5 shadcn).
 */

import { useEffect, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useNotify } from "../ui/NotifyProvider";
import { catalogSuppliers, type CatalogSupplier } from "../../lib/api-catalog";
import { ApiError } from "../../lib/api-client";

interface Props {
  open: boolean;
  supplier: CatalogSupplier | null;
  initialName?: string;
  onClose: () => void;
  onSaved: (supplier: CatalogSupplier) => void;
}

export function SupplierEditDialog({
  open,
  supplier,
  initialName,
  onClose,
  onSaved,
}: Props) {
  const notify = useNotify();
  const isEdit = !!supplier;
  const [displayName, setDisplayName] = useState("");
  const [inn, setInn] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [defaultDeliveryDays, setDefaultDeliveryDays] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (supplier) {
      setDisplayName(supplier.displayName);
      setInn(supplier.inn ?? "");
      setContactPhone(supplier.contactPhone ?? "");
      setContactEmail(supplier.contactEmail ?? "");
      setDefaultDeliveryDays(supplier.defaultDeliveryDays?.toString() ?? "");
      setNotes(supplier.notes ?? "");
    } else {
      setDisplayName(initialName ?? "");
      setInn("");
      setContactPhone("");
      setContactEmail("");
      setDefaultDeliveryDays("");
      setNotes("");
    }
    setError(null);
  }, [supplier, initialName, open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!displayName.trim()) {
      setError("Название обязательно.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        displayName: displayName.trim(),
        inn: inn.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        defaultDeliveryDays: defaultDeliveryDays
          ? parseInt(defaultDeliveryDays, 10)
          : undefined,
        notes: notes.trim() || undefined,
      };
      const saved = supplier
        ? await catalogSuppliers.update(supplier.id, payload)
        : await catalogSuppliers.create(payload);
      notify.success(supplier ? "Поставщик обновлён" : "Поставщик создан");
      onSaved(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Редактировать поставщика" : "Новый поставщик"}
          </DialogTitle>
        </DialogHeader>

        <form
          id="supplier-form"
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-col gap-3 overflow-y-auto"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="s-name">Название *</Label>
            <Input
              id="s-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={submitting}
              required
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="s-inn">ИНН</Label>
              <Input
                id="s-inn"
                value={inn}
                onChange={(e) => setInn(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="s-days">Срок доставки (дн.)</Label>
              <Input
                id="s-days"
                type="number"
                min={0}
                max={365}
                value={defaultDeliveryDays}
                onChange={(e) => setDefaultDeliveryDays(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="s-phone">Телефон</Label>
              <Input
                id="s-phone"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="s-email">Email</Label>
              <Input
                id="s-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="s-notes">Внутренние заметки</Label>
            <Textarea
              id="s-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              rows={2}
            />
          </div>
          {error ? <Alert severity="error">{error}</Alert> : null}
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          <Button type="submit" form="supplier-form" disabled={submitting}>
            {submitting ? "Сохраняем…" : isEdit ? "Сохранить" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
